import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { API_BASE_URL } from "@/api/helpers";
import { log } from "@/core/logger";
import { useMutation } from "@tanstack/react-query";
import type { Member } from "@/types/Member";

interface UpdateMemberNameParams {
  memberId: string;
  firstName: string;
  lastName: string;
}

/** Update the signed-in member's own first/last name via the existing PATCH /members/:id. */
export function useUpdateMemberName() {
  return useMutation({
    mutationFn: async ({ memberId, firstName, lastName }: UpdateMemberNameParams): Promise<Member> => {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ firstName, lastName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || "Failed to update name");
      }

      const data = await response.json();
      return data.member ?? data;
    },
    onError: (error: Error) => {
      log("useUpdateMemberName", "Error updating member name", error);
    },
  });
}
